import React, { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import axios from 'axios';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { API } from '../App';
import { Button } from '@/components/ui/button';
import { CheckCircle, Loader2, Package, ChevronRight, FileText, Box, Clock } from 'lucide-react';

const OrderSuccessPage = () => {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  const orderId = searchParams.get('order_id');
  const isInvoice = searchParams.get('payment') === 'invoice';
  
  const [status, setStatus] = useState(sessionId ? 'checking' : 'success');
  const [order, setOrder] = useState(null);
  const [pollCount, setPollCount] = useState(0);

  useEffect(() => {
    if (sessionId) {
      pollPaymentStatus();
    } else if (orderId) {
      fetchOrder(orderId);
    }
  }, [sessionId, orderId]);

  const pollPaymentStatus = async () => {
    const maxPolls = 10;
    
    if (pollCount >= maxPolls) {
      setStatus('pending');
      return;
    }

    try {
      const response = await axios.get(`${API}/payments/status/${sessionId}`);
      
      if (response.data.payment_status === 'paid') {
        setStatus('success');
        sessionStorage.removeItem('quote');
        sessionStorage.removeItem('quoteFiles');
        // Get order details
        const orderRes = await axios.get(`${API}/orders?status=confirmed`);
        if (orderRes.data.orders?.length > 0) {
          setOrder(orderRes.data.orders[0]);
        }
      } else if (response.data.status === 'expired') {
        setStatus('failed');
      } else {
        setPollCount(prev => prev + 1);
        setTimeout(pollPaymentStatus, 2000);
      }
    } catch (error) {
      console.error('Error checking payment status:', error);
      setPollCount(prev => prev + 1);
      setTimeout(pollPaymentStatus, 2000);
    }
  };

  const fetchOrder = async (id) => {
    try {
      const token = localStorage.getItem('auth_token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const response = await axios.get(`${API}/orders/${id}`, { headers });
      setOrder(response.data);
    } catch (error) {
      console.error('Error fetching order:', error);
    }
  };

  // Get file icon based on extension
  const getFileIcon = (filename) => {
    const ext = filename?.toLowerCase().split('.').pop();
    const colors = {
      stl: '#CCFF00',
      obj: '#00CCFF',
      '3mf': '#FF00CC',
      step: '#FFCC00',
      stp: '#FFCC00',
      iges: '#CC00FF',
      igs: '#CC00FF'
    };
    return colors[ext] || '#CCFF00';
  };

  return (
    <div className="min-h-screen bg-[#050505]" data-testid="order-success-page">
      <Navbar />
      
      <main className="pt-32 pb-16">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          {status === 'checking' && (
            <div className="py-16 text-center" data-testid="payment-checking">
              <Loader2 className="w-16 h-16 text-[#CCFF00] mx-auto animate-spin mb-6" />
              <h1 className="text-2xl font-bold text-white mb-2">Processing Payment...</h1>
              <p className="text-white/50">Please wait while we confirm your payment.</p>
            </div>
          )}

          {status === 'success' && (
            <div data-testid="payment-success">
              {/* Success Header */}
              <div className="text-center mb-12">
                <div className="w-20 h-20 bg-[#CCFF00]/10 rounded-full flex items-center justify-center mx-auto mb-6">
                  <CheckCircle className="w-10 h-10 text-[#CCFF00]" />
                </div>
                <h1 className="text-3xl font-['Chivo'] font-black text-white mb-2">
                  {isInvoice ? 'Order Submitted!' : 'Order Confirmed!'}
                </h1>
                {order && (
                  <p className="text-[#CCFF00] font-mono font-bold text-xl mb-4">
                    {order.order_id}
                  </p>
                )}
                <p className="text-white/70 max-w-md mx-auto">
                  {isInvoice 
                    ? 'Your order has been submitted. We\'ll review and send an invoice to your email.'
                    : 'Thank you for your order. You\'ll receive a confirmation email shortly.'
                  }
                </p>
              </div>

              {/* Order Summary Card */}
              {order && (
                <div className="bg-[#111] border border-white/10 rounded-sm overflow-hidden mb-8">
                  {/* Order Header */}
                  <div className="p-6 border-b border-white/10 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                    <div>
                      <p className="text-white/50 text-sm">Order ID</p>
                      <p className="text-white font-mono font-bold text-xl">{order.order_id}</p>
                    </div>
                    <div className="flex items-center gap-3">
                      {isInvoice ? (
                        <span className="px-3 py-1.5 bg-yellow-500/10 text-yellow-500 text-sm font-medium rounded-sm flex items-center gap-2">
                          <Clock className="w-4 h-4" />
                          Invoice Pending
                        </span>
                      ) : (
                        <span className="px-3 py-1.5 bg-[#CCFF00]/10 text-[#CCFF00] text-sm font-medium rounded-sm flex items-center gap-2">
                          <CheckCircle className="w-4 h-4" />
                          {order.payment_status === 'paid' ? 'Paid' : order.order_status}
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Order Items with Thumbnails */}
                  <div className="p-6">
                    <h3 className="text-white font-bold mb-4">Order Items ({order.items?.length || 0})</h3>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      {order.items?.map((item, index) => (
                        <div 
                          key={index}
                          className="bg-white/5 border border-white/10 rounded-sm p-4 flex items-start gap-4"
                        >
                          {/* Part Thumbnail */}
                          <div 
                            className="w-16 h-16 flex-shrink-0 rounded-sm flex items-center justify-center"
                            style={{ backgroundColor: `${getFileIcon(item.file_name)}15` }}
                          >
                            <Box 
                              className="w-8 h-8"
                              style={{ color: getFileIcon(item.file_name) }}
                            />
                          </div>
                          
                          {/* Part Details */}
                          <div className="flex-1 min-w-0">
                            <p className="text-white font-medium truncate" title={item.file_name}>
                              {item.file_name}
                            </p>
                            <div className="text-white/50 text-sm mt-1 space-y-0.5">
                              <p>Volume: <span className="font-mono">{item.volume_cm3?.toFixed(2)} cm³</span></p>
                              <p>Qty: <span className="font-mono">{item.quantity}</span></p>
                            </div>
                            <p className="text-[#CCFF00] font-mono font-bold mt-2">
                              ${item.total_price?.toFixed(2)}
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Order Totals */}
                  <div className="p-6 bg-white/5 border-t border-white/10">
                    <div className="space-y-2">
                      <div className="flex justify-between text-sm">
                        <span className="text-white/50">Subtotal</span>
                        <span className="text-white font-mono">${order.subtotal?.toFixed(2)}</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-white/50">Delivery</span>
                        <span className="text-white font-mono">${order.delivery_cost?.toFixed(2)}</span>
                      </div>
                      <div className="flex justify-between text-lg pt-3 border-t border-white/10">
                        <span className="text-white font-bold">Total</span>
                        <span className="text-[#CCFF00] font-bold font-mono">
                          ${order.total?.toFixed(2)} AUD
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Customer Info */}
                  <div className="p-6 border-t border-white/10">
                    <h3 className="text-white font-bold mb-3">Delivery Details</h3>
                    <div className="text-white/70 text-sm space-y-1">
                      <p>{order.customer_name}</p>
                      <p>{order.customer_email}</p>
                      {order.customer_phone && <p>{order.customer_phone}</p>}
                      {order.customer_address && <p>{order.customer_address}</p>}
                    </div>
                  </div>
                </div>
              )}

              {/* What's Next */}
              <div className="bg-[#111] border border-white/10 rounded-sm p-6 mb-8">
                <h3 className="text-white font-bold mb-4">What Happens Next?</h3>
                <div className="space-y-4">
                  <div className="flex items-start gap-4">
                    <div className="w-8 h-8 bg-[#CCFF00]/10 rounded-full flex items-center justify-center flex-shrink-0">
                      <span className="text-[#CCFF00] font-mono text-sm">1</span>
                    </div>
                    <div>
                      <p className="text-white font-medium">Order Review</p>
                      <p className="text-white/50 text-sm">Our team will review your files and confirm specifications if required</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="w-8 h-8 bg-[#CCFF00]/10 rounded-full flex items-center justify-center flex-shrink-0">
                      <span className="text-[#CCFF00] font-mono text-sm">2</span>
                    </div>
                    <div>
                      <p className="text-white font-medium">Production</p>
                      <p className="text-white/50 text-sm">Parts manufactured on our HP MJF printers</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="w-8 h-8 bg-[#CCFF00]/10 rounded-full flex items-center justify-center flex-shrink-0">
                      <span className="text-[#CCFF00] font-mono text-sm">3</span>
                    </div>
                    <div>
                      <p className="text-white font-medium">Quality Check & Shipping</p>
                      <p className="text-white/50 text-sm">100% inspection then shipped with tracking</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Actions */}
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link to="/dashboard">
                  <Button className="w-full sm:w-auto bg-[#CCFF00] text-black hover:bg-[#B3E600] font-bold uppercase tracking-wide rounded-none px-8">
                    <Package className="w-4 h-4 mr-2" />
                    Track Order
                  </Button>
                </Link>
                <Link to="/get-quote">
                  <Button variant="outline" className="w-full sm:w-auto border-white/20 text-white hover:bg-white/5 rounded-none px-8">
                    New Quote
                    <ChevronRight className="w-4 h-4 ml-2" />
                  </Button>
                </Link>
              </div>
            </div>
          )}

          {status === 'pending' && (
            <div className="py-16 text-center" data-testid="payment-pending">
              <div className="w-20 h-20 bg-yellow-500/10 rounded-full flex items-center justify-center mx-auto mb-6">
                <Loader2 className="w-10 h-10 text-yellow-500" />
              </div>
              <h1 className="text-3xl font-['Chivo'] font-black text-white mb-4">
                Payment Processing
              </h1>
              <p className="text-white/70 mb-8">
                Your payment is still being processed. You'll receive an email confirmation once complete.
              </p>
              <Link to="/">
                <Button variant="outline" className="border-white/20 text-white hover:bg-white/5 rounded-none">
                  Return Home
                </Button>
              </Link>
            </div>
          )}

          {status === 'failed' && (
            <div className="py-16 text-center" data-testid="payment-failed">
              <div className="w-20 h-20 bg-red-500/10 rounded-full flex items-center justify-center mx-auto mb-6">
                <span className="text-red-500 text-3xl">✕</span>
              </div>
              <h1 className="text-3xl font-['Chivo'] font-black text-white mb-4">
                Payment Failed
              </h1>
              <p className="text-white/70 mb-8">
                Your payment could not be processed. Please try again.
              </p>
              <Link to="/get-quote">
                <Button className="bg-[#CCFF00] text-black hover:bg-[#B3E600] font-bold uppercase tracking-wide rounded-none">
                  Try Again
                  <ChevronRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
            </div>
          )}
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default OrderSuccessPage;
